/**
 * Action-sheet rows (components/mobile/ActionSheet.svelte) and the tap
 * alternatives to drag and drop built from them (plans/mobile-v2.md §3.5).
 */
import type { IconName } from './icons';

export interface SheetAction {
  label: string;
  icon?: IconName;
  danger?: boolean;
  onSelect: () => void;
}

/** -1 moves an item up (earlier), 1 moves it down (later). */
export type MoveDirection = -1 | 1;

/**
 * A copy of `list` with the item at `index` swapped with its neighbour in
 * `direction`, or null when it is already at that end (or `index` is invalid).
 */
export function moveItem<T>(
  list: readonly T[],
  index: number,
  direction: MoveDirection,
): T[] | null {
  const target = index + direction;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return null;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/**
 * "Move up" / "Move down" rows for the item at `index` of a list of `count`
 * items: the tap path for drag-to-reorder. The first item gets no "Move up" and
 * the last no "Move down", so a single item gets none.
 */
export function moveUpDownActions(
  index: number,
  count: number,
  onMove: (direction: MoveDirection) => void,
): SheetAction[] {
  if (index < 0 || index >= count) return [];
  const actions: SheetAction[] = [];
  if (index > 0) actions.push({ label: 'Move up', icon: 'arrow-up', onSelect: () => onMove(-1) });
  if (index < count - 1) {
    actions.push({ label: 'Move down', icon: 'arrow-down', onSelect: () => onMove(1) });
  }
  return actions;
}
